/**
 * Mock Report Data Generator
 *
 * Generates realistic mock data for template preview
 * Used by TemplatePreview to show how variables will be replaced
 *
 * Groups use ordemQuestionario (1-indexed) for variable syntax: {{grupo[ordem].propriedade}}
 */

import type { GrupoForVariable, GrupoFeedback } from "../hooks/useQuestionarioGroups";

/**
 * Mock report data structure matching ReportData interface
 */
export interface MockReportData {
  empresa: {
    nomeFantasia: string;
    razaoSocial: string;
    cnpj: string;
    setores: string[];
  };
  questionario: {
    nome: string;
    descricao: string;
  };
  projeto: {
    nome: string;
  };
  resultados: {
    empresa: Array<{
      segment: string;
      value: number;
    }>;
    /** Média geral de todas as empresas (para radar_geral) */
    geral?: Array<{
      segment: string;
      value: number;
    }>;
    /** Média do segmento/categoria (para radar_segmento) */
    categoria?: Array<{
      segment: string;
      value: number;
    }>;
  };
  grupos: Array<{
    id: string;
    nome: string;
    descricao?: string;
    /** Order for template variables (1-indexed): {{grupo[ordemQuestionario].propriedade}} */
    ordemQuestionario: number;
    /** Feedbacks configured for this grupo (score-based) */
    feedbacks: GrupoFeedback[];
  }>;
  geradoPor: string;
  dataGeracao: Date;
}

/**
 * Result data structure for preview
 */
export interface PreviewResultado {
  segment: string;
  value: number;
}

/**
 * Generates mock report data for preview
 * @param grupos - Optional real grupos from questionário (if available)
 * @param realResultados - Optional real results from diagnostic (if available)
 * @returns Mock report data with realistic values
 */
export function generateMockReportData(
  grupos?: GrupoForVariable[],
  realResultados?: PreviewResultado[]
): MockReportData {
  // Use real grupos if provided, otherwise use mock grupos
  const mockGrupos = grupos && grupos.length > 0 ? mapGruposToMock(grupos) : generateMockGrupos();

  // Use real resultados if available, otherwise generate mock data
  let segmentData: PreviewResultado[];
  if (realResultados && realResultados.length > 0) {
    // Use real results
    segmentData = realResultados;
  } else {
    // Generate mock segment data for resultados.empresa
    segmentData = mockGrupos.map((g) => ({
      segment: g.nome,
      value: 65 + Math.random() * 25 // Random between 65-90
    }));
  }

  // Generate mock data for média geral (slightly lower than empresa)
  const geralData = segmentData.map((item) => ({
    segment: item.segment,
    value: Math.max(40, item.value - 5 - Math.random() * 10) // 5-15% lower than empresa
  }));

  // Generate mock data for média do segmento (similar to geral but different)
  const categoriaData = segmentData.map((item) => ({
    segment: item.segment,
    value: Math.max(35, item.value - 3 - Math.random() * 12) // 3-15% lower than empresa
  }));

  return {
    empresa: {
      nomeFantasia: "Acme Corporation",
      razaoSocial: "Acme Corporation Ltda",
      cnpj: "12.345.678/0001-90",
      setores: ["Tecnologia", "Inovação"]
    },
    questionario: {
      nome: "Diagnóstico de Maturidade em Inovação 2024",
      descricao: "Avaliação completa do nível de maturidade organizacional em processos de inovação"
    },
    projeto: {
      nome: "Projeto Acelera Inova - Transformação Digital"
    },
    resultados: {
      empresa: segmentData,
      geral: geralData,
      categoria: categoriaData
    },
    grupos: mockGrupos,
    geradoPor: "João Silva",
    dataGeracao: new Date()
  };
}

/**
 * Maps real grupos to mock format
 * @param grupos
 */
function mapGruposToMock(grupos: GrupoForVariable[]): MockReportData["grupos"] {
  return grupos.map((grupo) => ({
    id: grupo.id,
    nome: grupo.nome,
    descricao: grupo.descricao,
    ordemQuestionario: grupo.ordem,
    feedbacks: grupo.feedbacks || []
  }));
}

/**
 * Default mock feedbacks for grupos (used when no real feedbacks available)
 */
const DEFAULT_MOCK_FEEDBACKS: GrupoFeedback[] = [
  {
    title: "Iniciante",
    description: "A empresa está nos primeiros passos nestass dimensão. Há oportunidades significativas de melhoria.",
    start: 0,
    end: 40
  },
  {
    title: "Em Desenvolvimento",
    description: "A empresa demonstra progressoss nesta área, mas ainda há espaço para evolução.",
    start: 41,
    end: 70
  },
  {
    title: "Avançado",
    description: "A empresa possui maturidade elevadass nesta dimensão, com práticas consolidadas.",
    start: 71,
    end: 100
  }
];

/**
 * Generates mock grupos when no real grupos available
 * ordemQuestionario starts at 1 (1-indexed)
 */
function generateMockGrupos(): MockReportData["grupos"] {
  const mockGruposData = [
    { nome: "Estratégia e Visão" },
    { nome: "Processos de Inovação" },
    { nome: "Cultura Organizacional" },
    { nome: "Recursos e Infraestrutura" },
    { nome: "Gestão de Pessoas" },
    { nome: "Tecnologia e Ferramentas" },
    { nome: "Parcerias e Ecossistema" },
    { nome: "Resultados e Impacto" }
  ];

  return mockGruposData.map((g, index) => ({
    id: `mock-grupo-${index + 1}`,
    nome: g.nome,
    descricao: `Dimensão focada em ${g.nome.toLowerCase()}`,
    ordemQuestionario: index + 1, // 1-indexed
    feedbacks: DEFAULT_MOCK_FEEDBACKS
  }));
}

/**
 * Formats CNPJ for display
 * @param cnpj
 */
export function formatCNPJ(cnpj: string): string {
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

/**
 * Formats date for Brazilian format
 * @param date
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString("pt-BR");
}

/**
 * Formats percentage with 1 decimal place
 * @param value
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}
